import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@radix-ui/react-icons';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/ui/components/shadcn/dialog';
import Button from '@/shared/ui/components/basic/Button';
import { cn } from '@/shared/ui/utils/tailwind';

interface Props {
  opened: boolean;
  /** Eyebrow text, already translated. */
  eyebrow: string;
  /** Modal title, already translated. */
  title: string;
  /** Narrow layout: the card step is one column and does not need 880px. */
  narrow?: boolean;
  /** Blocks the scrim, ESC and the close button while a change is in flight. */
  locked?: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Footer contents — buttons, right-aligned by the shell. */
  footer?: ReactNode;
}

/**
 * The one modal behind every plan, card and cancellation flow.
 *
 * It replaces a stack: picking a plan used to open a comparison modal and
 * then a confirmation modal on top of it, and changing a card opened a
 * summary modal and then the form modal on top of that. Modes and steps
 * happen inside this shell instead, so a flow never puts a second dialog over
 * the first.
 *
 * Built on the shadcn DialogContent rather than the Modal adapter: the
 * adapter fixes one max-width scale and a body of `calc(100vh - 8rem)`,
 * neither of which fits a two-column review. The primitive keeps the focus
 * trap, the portal and the phone sheet.
 *
 * @param props - Component props.
 * @returns The modal shell.
 */
export function PlanFlowShell({
  opened, eyebrow, title, narrow, locked, onClose, children, footer,
}: Props) {
  const { t } = useTranslation('common');

  /** Swallow a close while the gateway is working, so a charge is never orphaned. */
  const handleOpenChange = (open: boolean) => {
    if (!open && !locked) onClose();
  };

  const guardWhileLocked = (event: Event) => {
    if (locked) event.preventDefault();
  };

  return (
    <Dialog open={opened} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="plan-flow-modal"
        onEscapeKeyDown={guardWhileLocked}
        onPointerDownOutside={guardWhileLocked}
        onInteractOutside={guardWhileLocked}
        className={cn(
          // The shell owns its own padding: header, body and footer each have
          // their own, and a wrapping p-6 would double every edge.
          // One scroll, the body. The primitive's phone sheet also scrolls,
          // and the two together were the double bar on a card change.
          'flex flex-col gap-0 p-0 overflow-hidden max-md:overflow-hidden',
          'max-h-[calc(100vh-32px)] md:max-w-[calc(100vw-32px)]',
          narrow ? 'md:w-[540px]' : 'md:w-[880px]',
        )}
      >
        <header
          className={cn(
            'flex shrink-0 items-start gap-(--space-3)',
            'p-(--space-5) border-b border-(--border-ultra-subtle)',
          )}
        >
          <div className="flex min-w-0 flex-col gap-(--space-1)">
            <span
              className={cn(
                'text-(length:--text-small) [font-weight:var(--font-semibold)]',
                'text-(--text-secondary)',
              )}
            >
              {eyebrow}
            </span>
            <DialogTitle
              className={cn(
                'font-(family-name:--font-family-alt-2)',
                'text-(length:--text-xl) [font-weight:var(--font-bold)]',
                'text-(--text-primary)',
              )}
            >
              {title}
            </DialogTitle>
          </div>
          {/* variant="none" + disableSize: the shell keeps the classes it
              already had, and what the shared Button adds on top is the
              focus ring it was missing. */}
          <Button
            variant="none"
            disableSize
            type="button"
            aria-label={t('a11y.close')}
            onClick={onClose}
            disabled={locked}
            data-testid="plan-flow-close"
            className={cn(
              'ms-auto flex shrink-0 items-center justify-center',
              'rounded-(--radius-md) border-0 bg-transparent p-(--space-2)',
              'text-(--text-tertiary) cursor-pointer',
              'hover:bg-(--surface-tertiary) hover:text-(--text-primary)',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <Cross2Icon width={15} height={15} />
          </Button>
        </header>

        <div
          data-testid="plan-flow-body"
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-(--space-4)',
            'overflow-y-auto overflow-x-hidden overscroll-contain',
            'px-(--space-5) py-(--space-4)',
          )}
        >
          {children}
        </div>

        {footer && (
          <footer
            className={cn(
              'flex shrink-0 flex-wrap items-center justify-end gap-(--space-3)',
              'px-(--space-5) py-(--space-4)',
              'border-t border-(--border-ultra-subtle)',
            )}
          >
            {footer}
          </footer>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PlanFlowShell;
